import { Box, useTheme } from '@mui/material';
import React, { FC } from 'react';
import ReactMarkdown from 'react-markdown';

import { hasDataExtension } from '../../lib/dataExtensions';
import { expandPath, isDifferentWebsite, retrievePath } from '../../lib/iris';
import Link from '../Link';

const routerLink = (link: any) => {
  const extendedLink = expandPath(link.href);
  const isDataLink = extendedLink && hasDataExtension(extendedLink);

  if (extendedLink && !isDataLink && !isDifferentWebsite(extendedLink)) {
    return <Link to={retrievePath(extendedLink) ?? '#'}>{link.children}</Link>;
  }

  return (
    <a href={link.href} rel="noopener noreferrer" target="_blank">
      {link.children}
    </a>
  );
};

const codePre = (link: any) => <code>{link.value}</code>;

interface MarkdownProps {
  noSpacing?: boolean;
  text?: string;
}

const customRenderers = {
  code: codePre,
  link: routerLink,
};

const Markdown: FC<MarkdownProps> = ({ noSpacing, text }) => {
  const theme = useTheme();

  if (!text) {
    return null;
  }

  return (
    <Box
      escapeHtml
      unwrapDisallowed
      component={ReactMarkdown}
      renderers={customRenderers}
      source={text}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        wordWrap: 'break-word',
        '& a': {
          '&:hover': {
            textDecoration: 'underline',
          },
          textDecoration: 'underline',
        },
        '& a,p,h1,h2,h3,h4,span': {
          wordBreak: 'break-word',
        },
        '& blockquote': {
          '& p': {
            marginBottom: 0,
          },
          '&::after': {
            backgroundColor: theme.palette.grey.light,
            bottom: 0,
            content: '""',
            display: 'block',
            height: '100%',
            left: '-.7em',
            position: 'absolute',
            top: 0,
            width: '.3em',
          },
          fontStyle: 'italic',
          marginBottom: '1em',
          marginLeft: '.7em',
          position: 'relative',
        },
        '& code': {
          backgroundColor: theme.palette.grey.xxLight,
          border: theme.greyBorder,
          fontFamily: 'monospace',
          fontSize: '.9em',
          marginBottom: '1em',
          overflow: 'auto',
          padding: '.2em .3em',
          whiteSpace: 'pre',
        },
        '& em': {
          fontStyle: 'italic',
        },
        '& h1': {
          fontSize: theme.typography.fontSizes.xxLarge,
        },
        '& h2': {
          fontSize: theme.typography.fontSizes.xLarge,
        },
        '& h3': {
          fontSize: theme.typography.fontSizes.large,
        },
        '& h4': {
          fontSize: theme.typography.fontSizes.medium,
        },
        '& h4,h3,h2,h1': {
          lineHeight: '1em',
          marginBottom: '.4em',
        },
        '& img': {
          maxWidth: '100%',
        },
        '& p:last-of-type': {
          marginBottom: noSpacing ? 0 : '1em',
        },
        '& strong': {
          fontWeight: 'bold',
        },
        '& ol': {
          listStyleType: 'decimal',
        },
        '& ul': {
          listStyleType: 'disc',
        },
        '& ul,ol': {
          '& li': {
            marginBottom: '.3em',
            marginLeft: '1.5em',
          },
          marginBottom: noSpacing ? 0 : '.5em',
          marginLeft: noSpacing ? 0 : undefined,
        },
        '&--inline': {
          '& a,p': {
            display: 'inline',
          },
          display: 'inline',
        },
      }}
    />
  );
};

export default Markdown;
